import { Button, Modal, useToast } from "@design";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  type BudgetConfigResponse,
  createBudgetConfigMutation,
  errorMessage,
  listBudgetConfigsQueryKey,
  listTeamsOptions,
  schemas,
  updateBudgetConfigMutation,
} from "@/api";
import { UserSearchCombobox } from "@/components/build/UserSearchCombobox";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { BUDGET_SCOPES, type BudgetScope, useBudgetTargetName } from "./budgetScope";

// Amount and scope rules come from the generated BudgetConfigCreate schema.
const create = schemas.zBudgetConfigCreate.shape;

/** `editing`: only the amount can change, so the (hidden) scope target isn't validated. */
function useBudgetSchema(editing: boolean) {
  const { t } = useTranslation("adminUsage");
  return z
    .object({
      scope: create.scope,
      teamUuid: z.string(),
      userUuid: z.string(),
      userName: z.string(),
      amount: z.string().refine((v) => v.trim() !== "" && create.monthlyBudgetUsd.safeParse(Number(v)).success, {
        message: t("budgets.form.amountInvalid"),
      }),
    })
    .superRefine((value, ctx) => {
      if (editing) return;
      if (value.scope === "team" && !value.teamUuid) {
        ctx.addIssue({ code: "custom", path: ["teamUuid"], message: t("budgets.form.teamRequired") });
      }
      if (value.scope === "user" && !value.userUuid) {
        ctx.addIssue({ code: "custom", path: ["userUuid"], message: t("budgets.form.userRequired") });
      }
    });
}
type BudgetValues = z.infer<ReturnType<typeof useBudgetSchema>>;

/** Create (no `budget`) or edit (amount only) a budget limit. Mount when open. */
export function BudgetFormModal({ budget, onClose }: Readonly<{ budget?: BudgetConfigResponse; onClose: () => void }>) {
  const { t } = useTranslation("adminUsage");
  const toast = useToast();
  const queryClient = useQueryClient();
  const targetName = useBudgetTargetName();
  const schema = useBudgetSchema(Boolean(budget));
  const createBudget = useMutation(createBudgetConfigMutation());
  const updateBudget = useMutation(updateBudgetConfigMutation());

  const form = useAppForm({
    defaultValues: {
      scope: (budget?.scope as BudgetScope | undefined) ?? "platform",
      teamUuid: "",
      userUuid: "",
      userName: "",
      amount: budget ? String(budget.monthlyBudgetUsd) : "",
    } as BudgetValues,
    validators: { onChange: schema, onSubmit: schema },
    onSubmit: async ({ value }) => {
      const monthlyBudgetUsd = Number(value.amount);
      try {
        if (budget) {
          await updateBudget.mutateAsync({ path: { budget_id: budget.id }, body: { monthlyBudgetUsd } });
        } else {
          await createBudget.mutateAsync({
            body: {
              scope: value.scope,
              monthlyBudgetUsd,
              teamUuid: value.scope === "team" ? value.teamUuid : null,
              userUuid: value.scope === "user" ? value.userUuid : null,
            },
          });
        }
        await queryClient.invalidateQueries({ queryKey: listBudgetConfigsQueryKey() });
        toast.success(t(budget ? "budgets.form.updated" : "budgets.form.created"));
        onClose();
      } catch (error) {
        toast.error(errorMessage(error, t("budgets.form.failed")));
      }
    },
  });

  const scope = useStore(form.store, (s) => s.values.scope);
  const submitted = useStore(form.store, (s) => s.submissionAttempts > 0);
  const teams = useQuery({ ...listTeamsOptions({ query: { page_size: 100 } }), enabled: !budget && scope === "team" });
  const teamOptions = (teams.data?.data ?? []).map((team) => ({ value: team.uuid, label: team.name }));

  return (
    <Modal
      open
      onClose={onClose}
      title={budget ? t("budgets.form.editTitle", { name: targetName(budget) }) : t("budgets.form.createTitle")}
      actions={
        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(submitting) => (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t("budgets.form.cancel")}
              </Button>
              <Button variant="primary" loading={submitting} onClick={() => form.handleSubmit()}>
                {budget ? t("budgets.form.save") : t("budgets.form.create")}
              </Button>
            </>
          )}
        </form.Subscribe>
      }
    >
      <form
        aria-label={t("budgets.form.label")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        {!budget && (
          <form.AppField name="scope">
            {(f) => (
              <f.SelectField
                label={t("budgets.form.scope")}
                options={BUDGET_SCOPES.map((scope) => ({ value: scope, label: t(`budgets.scope.${scope}`) }))}
              />
            )}
          </form.AppField>
        )}
        {!budget && scope === "team" && (
          <form.AppField name="teamUuid">
            {(f) => (
              <f.SelectField
                label={t("budgets.form.team")}
                required
                placeholder={t("budgets.form.teamPlaceholder")}
                options={teamOptions}
              />
            )}
          </form.AppField>
        )}
        {!budget && scope === "user" && (
          <form.Field name="userUuid">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Text as="span" variant="body-sm">
                  {t("budgets.form.user")}
                </Text>
                <UserSearchCombobox
                  onSelect={(user) => {
                    form.setFieldValue("userName", user.name || user.email);
                    field.handleChange(user.uuid);
                  }}
                />
                <form.Subscribe selector={(s) => s.values.userName}>
                  {(name) =>
                    name ? (
                      <Text variant="caption" tone="secondary">
                        {t("budgets.form.selectedUser", { name })}
                      </Text>
                    ) : null
                  }
                </form.Subscribe>
                {field.state.meta.errors.length > 0 && submitted && (
                  <Text variant="caption" tone="error">
                    {t("budgets.form.userRequired")}
                  </Text>
                )}
              </div>
            )}
          </form.Field>
        )}
        <form.AppField name="amount">{(f) => <f.TextField label={t("budgets.form.amount")} required />}</form.AppField>
      </form>
    </Modal>
  );
}
